import {useRef} from 'react';
import {
    Alert,
    Button,
    Group,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useProviderConnections} from '../hooks/useProviderConnections';
import {useProviderConnectionsSearch} from '../hooks/useProviderConnectionsQuery';
import {useProviderConnectionsTableColumns} from '../hooks/useProviderConnectionsTableColumns';
import {ProviderConnection} from '../types';
import config from '../config';

// FNP-1248 — the index table.
//
// ⚠️ NO ROW SELECTION AND NO BATCH DELETE, DELIBERATELY.
//
// The DELETE pattern doc's shape is checkbox selection plus a toolbar delete.
// That is wrong for this entity: disconnecting MUST go through
// `previewProviderDisconnect` and render its consequences, and a preview is
// per-connection — there is no batch preview on the schema and no honest way to
// summarise four tenants' worth of consequences in one dialog. Disconnect is
// reached from the detail page, where the preview has room to be read.

export const ProviderConnectionsTable = () => {

    const {columns} = useProviderConnectionsTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchRecords,
        reload,
        isReloading,
    } = useProviderConnections();

    // Same module-scoped store the list hook filters on (FNP-815) — this
    // component owns the search input, the hook owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useProviderConnectionsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // The three empty states mean DIFFERENT things and `noRecordsText` is one
    // string, so the distinction is branched here rather than flattened into a
    // single message that sends a tenant hunting for a bug that is not there.
    const searching = keyword.trim() !== '';

    // FNP-1265 — ⚠️ ONLY WHEN THE COUNT IS ACTUALLY KNOWN TO BE ZERO.
    //
    // A FAILED query yields an empty `visibleItems` exactly like a genuine zero
    // does, and the two are indistinguishable by the time the table sees them.
    // Claiming "no providers connected yet" off a rejected request told a
    // tenant who HAS a connection that they have none — on screen, directly
    // under a banner saying the request failed, so the page contradicted
    // itself. Same shape as the zones module's `noConnectionsAtAll`
    // (FNP-1249): loading and failed are both "not known", not "zero".
    const countIsKnownZero = !loading && !isError;

    let noRecordsText = I18n.get('No DNS providers connected yet. Connect one to start managing zones.');
    if (searching) {
        // Outranks the failure branch: it describes what the tenant just
        // typed, which is the more specific answer, and a keyword can only
        // narrow rows that were successfully loaded in the first place.
        noRecordsText = I18n.get('No connection matches this search');
    } else if (!countIsKnownZero) {
        // ⚠️ STATES NOTHING ABOUT HOW MANY CONNECTIONS EXIST, because nothing
        // is known. The banner above already names the failure and owns the
        // Retry, so repeating either here would duplicate it; saying "none"
        // would contradict it. This is the one honest sentence available: it
        // describes the table, not the tenant's account.
        noRecordsText = I18n.get('Your connections could not be loaded');
    }
    // ⚠️ THE TRUE EMPTY STATE (the default above) NAMES THE NEXT ACTION. This
    // module exists because there was no way to create a connection from the
    // product, so a tenant arriving at an empty table is the expected first
    // visit — a bare "no records" would leave them exactly where they started.

    // FNP-1372 — ⚠️ CHOOSING THE RIGHT MESSAGE IS NOT THE SAME AS RENDERING IT
    // ONLY WHEN IT APPLIES.
    //
    // Every branch above is correct about WHICH string to use, and the bug
    // survived all of them: `noRecordsText` was handed to the table
    // unconditionally, and mantine-datatable puts that node in the DOM even
    // when it has records to show. Observed in Chrome on 2026-09-03 and again
    // 2026-09-05 — four connections on screen with "No DNS providers connected
    // yet" in the extracted page text, three rows above "4 connections loaded".
    //
    // ⚠️ IT IS NOT PAINTED, WHICH IS EXACTLY WHY IT LASTED. A screenshot shows
    // nothing wrong and neither does a human reading the page. But a screen
    // reader walks the DOM, so a blind tenant is told they have no connections
    // while a sighted one sees four.
    //
    // ⚠️ `noRecordsText={undefined}` IS NOT THE FIX, and the browser said so.
    // Withholding the string only falls back to the library's own default, so
    // the page then carried "No records" beside four rows — the same defect
    // with blander wording. `emptyState` replaces the whole node, so an empty
    // fragment removes it from the DOM outright.
    const tableIsEmpty = visibleItems.length === 0;

    return (
        <SimpleGrid verticalSpacing={'xl'} m={'sm'}>

            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {/* The QUERY failed — nothing is known, which is distinct from a
                tenant having no connections. Rendering the empty state here
                would tell them they have none when the truth is that we could
                not find out. */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load your connections')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return your provider connections. This is not a provider problem — the request itself failed.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={reload}
                            loading={isReloading}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            <MantineDataTable<ProviderConnection>
                columns={columns}
                records={visibleItems}
                idAccessor={'id'}
                // Client-side sorting, over the pages loaded so far — the same
                // scope the keyword works on. Token pagination cannot reorder
                // rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                {...(tableIsEmpty ? {} : {emptyState: <></>})}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered
                // one.
                filterTerm={searching ? keyword : undefined}
                noun={'connection'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchRecords}
            />

        </SimpleGrid>
    );
};
